import React from 'react';
import { Box, Button, CircularProgress, IconButton, Paper, Typography, Tooltip } from '@mui/material';
import {
  Description as PlanIcon,
  GitHub as GitHubIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import { ArtifactStatus, useLocks } from '../hooks/useDeploymentData';
import { getColors, getElevation, rounded } from '../theme/tokens';
import { reduceMotionSx, transition } from '../theme/motion';
import { sortEnvNames } from '../lib/grouping';
import { recordLockKey } from '../lib/locks';
import { hasWatchState } from '../theme/watchTokens';
import { buildDeployCommand } from '../../utils/serviceParser';
import StatusPill from './StatusPill';
import WatchPill from './WatchPill';
import LockBadge from './LockBadge';
import { formatTimestamp, timeAgo } from '../lib/format';

/** Shared compact style for the per-card TF Plan / Github buttons. */
const linkButtonSx = {
  minWidth: 0,
  py: 0.1,
  px: 0.85,
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
};

export interface EnvMatrixProps {
  /** Representative record per environment name. */
  byEnv: Map<string, DeploymentRecord>;
  /** Optional click-through per environment cell. */
  onEnvClick?: (env: string, record: DeploymentRecord) => void;
  /** Terraform plan link per env (falls back to the record's own s3_plan_link). */
  planLinks?: Map<string, string>;
  /** Per-env github-commit button state (artifact lookup, fetched on click). */
  github?: Map<string, { status: ArtifactStatus; url?: string }>;
  /** Trigger the on-demand artifact fetch for an env's record. */
  onGithubFetch?: (record: DeploymentRecord) => void;
}

/**
 * "What's where" — one card per environment showing the current version and
 * status for a single service. The core answer of the service page.
 */
const EnvMatrix: React.FC<EnvMatrixProps> = ({
  byEnv,
  onEnvClick,
  planLinks,
  github,
  onGithubFetch,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const locks = useLocks();
  const envs = sortEnvNames(Array.from(byEnv.keys()));

  if (envs.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: 1.5,
      }}
    >
      {envs.map(env => {
        const record = byEnv.get(env)!;
        const lockKey = recordLockKey(record);
        const lock = lockKey && locks.size ? locks.get(lockKey) : undefined;
        return (
          <Paper
            key={env}
            variant="outlined"
            onClick={onEnvClick ? () => onEnvClick(env, record) : undefined}
            role={onEnvClick ? 'button' : undefined}
            tabIndex={onEnvClick ? 0 : undefined}
            onKeyDown={
              onEnvClick
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onEnvClick(env, record);
                    }
                  }
                : undefined
            }
            sx={{
              p: 2,
              borderRadius: `${rounded.lg}px`,
              borderColor: c.hairline,
              backgroundColor: c.surface,
              cursor: onEnvClick ? 'pointer' : 'default',
              transition: onEnvClick
                ? transition(['border-color', 'box-shadow', 'transform'])
                : undefined,
              '&:hover': onEnvClick
                ? {
                    borderColor: c.inkFaint,
                    transform: 'translateY(-2px)',
                    boxShadow: getElevation(theme.palette.mode).soft,
                  }
                : undefined,
              ...reduceMotionSx,
              '&:focus-visible': onEnvClick
                ? { outline: `2px solid ${c.primary}`, outlineOffset: 2 }
                : undefined,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  color: c.inkMuted,
                }}
                noWrap
              >
                {env}
              </Typography>
              {record.region && (
                <Typography variant="caption" sx={{ color: c.inkFaint }} noWrap>
                  {record.region}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <StatusPill status={record.status} />
            </Box>
            <Typography
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: c.ink,
                mb: 0.5,
              }}
              noWrap
              title={record.version}
            >
              {record.version || '—'}
            </Typography>
            <Tooltip title={formatTimestamp(record.timestamp)} placement="top" arrow>
              <Typography variant="caption" sx={{ color: c.inkMuted }}>
                {timeAgo(record.timestamp)}
                {record.buildUser ? ` · ${record.buildUser}` : ''}
              </Typography>
            </Tooltip>
            {(hasWatchState(record.watch_state) || lock) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                <WatchPill record={record} />
                {lock && <LockBadge lock={lock} />}
              </Box>
            )}
            {(() => {
              const gh = github?.get(env);
              const planLink = planLinks?.get(env) ?? record.s3_plan_link;
              const deployCmd = buildDeployCommand(record);
              if (!planLink && !gh && !deployCmd) return null;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                  {planLink && (
                    <Tooltip title="View terraform plan" arrow>
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={planLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        startIcon={<PlanIcon sx={{ fontSize: 14 }} />}
                        sx={linkButtonSx}
                      >
                        TF Plan
                      </Button>
                    </Tooltip>
                  )}
                  {gh && gh.status === 'ready' && gh.url && (
                    <Tooltip title="View commit on GitHub" arrow>
                      <Button
                        size="small"
                        variant="outlined"
                        component="a"
                        href={gh.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        startIcon={<GitHubIcon sx={{ fontSize: 14 }} />}
                        sx={linkButtonSx}
                      >
                        Github
                      </Button>
                    </Tooltip>
                  )}
                  {gh && gh.status !== 'ready' && (
                    <Tooltip
                      title={
                        gh.status === 'loading'
                          ? 'Fetching link…'
                          : gh.status === 'none'
                            ? 'No GitHub link found'
                            : gh.status === 'error'
                              ? 'Unable to fetch'
                              : 'Get link'
                      }
                      arrow
                    >
                      {/* span wrapper so the tooltip still shows on disabled states */}
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={gh.status === 'loading' || gh.status === 'none'}
                          onClick={e => {
                            e.stopPropagation();
                            onGithubFetch?.(record);
                          }}
                          startIcon={
                            gh.status === 'loading' ? (
                              <CircularProgress size={12} />
                            ) : (
                              <GitHubIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          sx={linkButtonSx}
                        >
                          {gh.status === 'none' ? 'No repo' : 'Github'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {deployCmd && (
                    <Tooltip title={`Copy deploy command (${deployCmd})`} arrow>
                      <IconButton
                        size="small"
                        aria-label="Copy deploy command"
                        onClick={e => {
                          e.stopPropagation();
                          navigator.clipboard?.writeText(deployCmd);
                        }}
                      >
                        <TerminalIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })()}
          </Paper>
        );
      })}
    </Box>
  );
};

export default EnvMatrix;
